/**
 * Database operations for Peke Adventures Survey Responses
 * Privacy-conscious: No personal identifying information collected
 */

import { supabase, getSessionId } from './supabase';

export interface PekeSurveyResponse {
  sessionId: string;
  feelingEmoji: string;
  feelingLabel: string;
  favoritePartEmoji: string;
  favoritePartLabel: string;
  futureStoryIdea: string | null;
  language: 'en' | 'es';
  gameDurationSeconds?: number;
  userAgent?: string;
  screenSize?: string;
}

/**
 * Save Peke survey response to Supabase
 * @param response - Survey response data
 * @returns Success status and any error
 */
export async function savePekeSurveyResponse(
  response: Omit<PekeSurveyResponse, 'sessionId' | 'userAgent' | 'screenSize'>
) {
  try {
    const fullResponse: PekeSurveyResponse = {
      ...response,
      sessionId: getSessionId(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      screenSize: getScreenSize()
    };

    const { data, error } = await supabase
      .from('peke_survey_responses')
      .insert({
        session_id: fullResponse.sessionId,
        feeling_emoji: fullResponse.feelingEmoji,
        feeling_label: fullResponse.feelingLabel,
        favorite_part_emoji: fullResponse.favoritePartEmoji,
        favorite_part_label: fullResponse.favoritePartLabel,
        future_story_idea: fullResponse.futureStoryIdea || null,
        language: fullResponse.language,
        game_duration_seconds: fullResponse.gameDurationSeconds || null,
        user_agent: fullResponse.userAgent || null,
        screen_size: fullResponse.screenSize || null
      });

    if (error) {
      console.error('Error saving Peke survey response:', error);
      return { success: false, error };
    }

    console.log('Peke survey response saved successfully:', data);
    return { success: true, data };
  } catch (error) {
    console.error('Unexpected error saving Peke survey response:', error);
    return { success: false, error };
  }
}

/**
 * Get device screen size category for analytics
 */
export function getScreenSize(): string {
  if (typeof window === 'undefined') return 'unknown';

  const width = window.innerWidth;
  if (width < 640) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}
